import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Head } from "./shared";
import { Loader2, Sparkles, Send, Link2, Plus, Copy, Power, CheckCircle2, XCircle, Inbox } from "lucide-react";

const STATUS_STYLE = {
  new: "text-blue-300 bg-blue-500/10 border-blue-500/20",
  drafted: "text-gold bg-gold/10 border-gold/25",
  responded: "text-green-300 bg-green-500/10 border-green-500/20",
  won: "text-green-300 bg-green-500/15 border-green-500/30",
  lost: "text-gray-400 bg-white/5 border-white/10",
};

export function Leads() {
  const [configs, setConfigs] = useState([]);
  const [eligible, setEligible] = useState([]);
  const [leads, setLeads] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const loadConfigs = useCallback(() => {
    api.get("/admin/intake-configs").then(({ data }) => { setConfigs(data.configs || []); setEligible(data.eligible_subscriptions || []); }).catch(() => {});
  }, []);
  const loadLeads = useCallback(() => {
    api.get(`/admin/leads${filter ? `?status=${filter}` : ""}`).then(({ data }) => { setLeads(data.leads || []); setCounts(data.counts || {}); }).catch(() => {});
  }, [filter]);
  useEffect(() => { loadConfigs(); }, [loadConfigs]);
  useEffect(() => { loadLeads(); }, [loadLeads]);

  const origin = window.location.origin;

  return (
    <div data-testid="admin-leads">
      <Head title="Leads & Estimate Intake" desc="AI-drafted first replies you review & send. Gated to AI Growth clients." />

      {/* Intake links */}
      <div className="bg-navy-800 border border-white/10 p-5 mb-6">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm text-white font-semibold flex items-center gap-2"><Link2 size={15} className="text-gold" /> Estimate Intake Links</div>
          <button onClick={() => setShowNew((s) => !s)} data-testid="new-intake-btn" className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 bg-gold text-navy font-semibold hover:bg-gold-light"><Plus size={13} /> New link</button>
        </div>
        {showNew && <NewIntakeForm eligible={eligible} onCreated={() => { setShowNew(false); loadConfigs(); }} />}
        {configs.length === 0 ? (
          <div className="text-gray-500 text-xs">No intake links yet. Create one for an AI Growth client (or for testing).</div>
        ) : (
          <div className="space-y-2" data-testid="intake-list">
            {configs.map((c) => (
              <div key={c.id} className="flex flex-wrap items-center justify-between gap-2 text-xs border border-white/10 p-2.5">
                <div>
                  <span className="text-white font-medium">{c.business_name}</span>
                  <span className="text-gray-500 ml-2">{c.lead_count} lead{c.lead_count === 1 ? "" : "s"}</span>
                  {!c.active && <span className="ml-2 text-red-300">(inactive)</span>}
                  <div className="font-mono text-gray-400 mt-0.5">{origin}/estimate/{c.slug}</div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => { navigator.clipboard.writeText(`${origin}/estimate/${c.slug}`); toast.success("Link copied"); }} className="inline-flex items-center gap-1 text-gray-300 hover:text-white"><Copy size={12} /> Copy</button>
                  <button onClick={() => api.post(`/admin/intake-configs/${c.id}/toggle`).then(loadConfigs)} className="inline-flex items-center gap-1 text-gray-300 hover:text-white"><Power size={12} /> {c.active ? "Disable" : "Enable"}</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Status filters */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="lead-filters">
        {["", "new", "drafted", "responded", "won", "lost"].map((s) => (
          <button key={s || "all"} onClick={() => setFilter(s)} data-testid={`filter-${s || "all"}`}
            className={`px-3 py-1.5 text-xs border ${filter === s ? "bg-gold text-navy border-gold font-semibold" : "border-white/15 text-gray-300 hover:text-white"}`}>
            {s ? `${s} (${counts[s] || 0})` : "All"}
          </button>
        ))}
      </div>

      {/* Leads list */}
      {leads.length === 0 ? (
        <div className="text-gray-500 text-sm bg-navy-800 border border-white/10 p-8 text-center flex flex-col items-center gap-2" data-testid="leads-empty"><Inbox size={22} /> No leads yet.</div>
      ) : (
        <div className="space-y-2" data-testid="leads-list">
          {leads.map((l) => (
            <button key={l.id} onClick={() => setSelected(l)} data-testid={`lead-${l.id}`}
              className="w-full text-left flex flex-wrap items-center justify-between gap-2 bg-navy-800 border border-white/10 hover:border-gold/30 p-3">
              <div>
                <span className="text-white text-sm font-medium">{l.name}</span>
                <span className="text-gray-500 text-xs ml-2">{l.service || "—"} · {l.business_name}</span>
                <div className="text-gray-500 text-xs">{l.email} · {new Date(l.created_at).toLocaleDateString()}</div>
              </div>
              <span className={`text-[11px] px-2 py-0.5 border ${STATUS_STYLE[l.status]}`}>{l.status}</span>
            </button>
          ))}
        </div>
      )}

      {selected && <LeadDrawer lead={selected} onClose={() => setSelected(null)} onChanged={() => { loadLeads(); loadConfigs(); }} />}
    </div>
  );
}

function NewIntakeForm({ eligible, onCreated }) {
  const [f, setF] = useState({ business_name: "", subscription_id: "", services: "" });
  const [busy, setBusy] = useState(false);
  const create = async () => {
    if (!f.business_name.trim()) { toast.error("Business name required"); return; }
    setBusy(true);
    try {
      const payload = { business_name: f.business_name, services: f.services };
      if (f.subscription_id) payload.subscription_id = f.subscription_id;
      await api.post("/admin/intake-configs", payload);
      toast.success("Intake link created");
      onCreated();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const ic = "bg-navy border border-white/15 text-white p-2 text-xs focus:border-gold focus:outline-none";
  return (
    <div className="mb-4 p-3 border border-gold/20 bg-gold/[0.03] space-y-2" data-testid="new-intake-form">
      <div className="grid sm:grid-cols-3 gap-2">
        <input className={ic} placeholder="Business name *" value={f.business_name} onChange={(e) => setF({ ...f, business_name: e.target.value })} data-testid="intake-business-name" />
        <input className={ic} placeholder="Services (optional)" value={f.services} onChange={(e) => setF({ ...f, services: e.target.value })} />
        <select className={ic} value={f.subscription_id} onChange={(e) => setF({ ...f, subscription_id: e.target.value })} data-testid="intake-sub-select">
          <option value="">Link AI Growth client (optional)</option>
          {eligible.map((s) => <option key={s.id} value={s.id}>{s.org_name || s.email}</option>)}
        </select>
      </div>
      <button onClick={create} disabled={busy} data-testid="intake-create-btn" className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 bg-gold text-navy font-semibold disabled:opacity-50">
        {busy ? <Loader2 className="animate-spin" size={12} /> : <Plus size={12} />} Create link
      </button>
    </div>
  );
}

function LeadDrawer({ lead, onClose, onChanged }) {
  const [subject, setSubject] = useState(lead.ai_draft?.subject || lead.response?.subject || "");
  const [body, setBody] = useState(lead.ai_draft?.body || lead.response?.body || "");
  const [busy, setBusy] = useState("");
  const sent = lead.status === "responded" || !!lead.response;

  const draft = async () => {
    setBusy("draft");
    try {
      const { data } = await api.post(`/admin/leads/${lead.id}/draft`);
      setSubject(data.draft.subject); setBody(data.draft.body);
      toast.success(`AI drafted a reply (${data.draft.provider || "AI"}). Review & send.`);
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };
  const send = async () => {
    if (!subject.trim() || !body.trim()) { toast.error("Nothing to send"); return; }
    setBusy("send");
    try {
      await api.post(`/admin/leads/${lead.id}/send`, { subject, body });
      toast.success(`Reply sent to ${lead.email}`);
      onChanged(); onClose();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };
  const setStatus = async (status) => {
    try { await api.post(`/admin/leads/${lead.id}/status`, { status }); toast.success(`Marked ${status}`); onChanged(); onClose(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/60" onClick={onClose} data-testid="lead-drawer">
      <div className="w-full max-w-xl bg-navy h-full overflow-y-auto border-l border-white/10 p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-xl text-white">{lead.name}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><XCircle size={20} /></button>
        </div>
        <div className="bg-navy-800 border border-white/10 p-4 text-sm text-gray-300 space-y-1 mb-5">
          <div><span className="text-gray-500">Business:</span> {lead.business_name}</div>
          <div><span className="text-gray-500">Email:</span> {lead.email} {lead.phone && `· ${lead.phone}`}</div>
          {lead.service && <div><span className="text-gray-500">Needs:</span> {lead.service}</div>}
          {lead.address && <div><span className="text-gray-500">Area:</span> {lead.address}</div>}
          {lead.timeline && <div><span className="text-gray-500">Timeline:</span> {lead.timeline}</div>}
          {lead.details && <div><span className="text-gray-500">Details:</span> {lead.details}</div>}
        </div>

        <div className="flex items-center justify-between mb-2">
          <div className="text-xs uppercase tracking-wider text-gray-500">AI-drafted reply</div>
          <button onClick={draft} disabled={!!busy} data-testid="lead-draft-btn" className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 bg-gold text-navy font-semibold hover:bg-gold-light disabled:opacity-50">
            {busy === "draft" ? <Loader2 className="animate-spin" size={12} /> : <Sparkles size={12} />} {lead.ai_draft ? "Re-draft" : "Draft with AI"}
          </button>
        </div>
        <input className="w-full bg-navy-800 border border-white/15 text-white p-2.5 text-sm mb-2 focus:border-gold focus:outline-none" placeholder="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="lead-subject" />
        <textarea rows={9} className="w-full bg-navy-800 border border-white/15 text-white p-2.5 text-sm mb-3 focus:border-gold focus:outline-none" placeholder="AI draft appears here — edit before sending." value={body} onChange={(e) => setBody(e.target.value)} data-testid="lead-body" />
        <p className="text-[11px] text-gray-500 mb-4">Nothing is sent until you click Send. No pricing is included by design.</p>

        <div className="flex flex-wrap gap-2">
          <button onClick={send} disabled={!!busy || !body.trim()} data-testid="lead-send-btn" className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-green-500 text-navy hover:bg-green-400 disabled:opacity-40">
            {busy === "send" ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />} {sent ? "Send again" : "Approve & Send"}
          </button>
          <button onClick={() => setStatus("won")} data-testid="lead-won-btn" className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-green-500/30 text-green-300 hover:bg-green-500/10"><CheckCircle2 size={14} /> Won</button>
          <button onClick={() => setStatus("lost")} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-white/15 text-gray-400 hover:bg-white/5"><XCircle size={14} /> Lost</button>
        </div>
        {lead.response?.sent_at && <div className="text-[11px] text-green-300 mt-3">✓ Last sent {new Date(lead.response.sent_at).toLocaleString()}</div>}
      </div>
    </div>
  );
}
